import { createIdempotencyKey, fetchJson } from "./http/utils.js";
import { safeRedirect, safeRedirectUrl } from "./http/redirect.js";

const LOGIN_ENDPOINT = "/api/method/cita_events.api.core.login.secure_login";

function initLoginPage() {
  if (window.__citaLoginBundleInitialized) {
    return;
  }
  window.__citaLoginBundleInitialized = true;

  let isSubmitting = false;

  const form = document.getElementById("login-form");
  if (!form) {
    return;
  }

  const emailInput = document.getElementById("email");
  const secretInput = document.getElementById("password");
  const btn = document.getElementById("login-button");
  const btnText = document.getElementById("login-btn-text");
  const spinner = document.getElementById("login-spinner");
  const passwordLabel = document.getElementById("password-label");
  const passwordHint = document.getElementById("password-hint");
  const twoFactorPrompt = document.getElementById("twofactor-prompt");
  const forgotLink = document.getElementById("forgot-password-link");
  const toggleBtn = document.getElementById("toggle-password");
  const toggleText = document.getElementById("toggle-text");
  const toggleIconPath = document.getElementById("toggle-icon-path");
  const toggleIconCircle = document.getElementById("toggle-icon-circle");
  const redirectHint = document.querySelector('input[name="redirect"]')?.value || "/start";

  const showToast = window.showToast?.bind(window);
  const removeToast = window.removeToast?.bind(window);
  const twoFactorState = {
    active: false,
    tmpId: "",
  };

  {
    const pwToggle = document.getElementById("toggle-password");
    let visible = false;

    pwToggle?.addEventListener("click", () => {
      const pw = document.getElementById("password");
      const iconPath = document.getElementById("toggle-icon-path");
      const iconCircle = document.getElementById("toggle-icon-circle");
      const txt = document.getElementById("toggle-text");

      visible = !visible;
      pwToggle.setAttribute("aria-pressed", String(visible));
      if (pw) {
        pw.type = visible ? "text" : "password";
      }

      if (visible) {
        iconPath?.setAttribute("d", "M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.17-6.36M1 1l22 22");
        iconCircle?.setAttribute("r", "0");
        if (txt) {
          txt.textContent = "Verbergen";
        }
      } else {
        iconPath?.setAttribute("d", "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z");
        iconCircle?.setAttribute("r", "3");
        if (txt) {
          txt.textContent = "Anzeigen";
        }
      }
    });
  }

  const setSubmitLabel = (isTwoFactor) => {
    if (btnText) {
      btnText.textContent = isTwoFactor ? "Code bestätigen" : "Jetzt einloggen";
    }
  };

  const resetTwoFactorMode = () => {
    twoFactorState.active = false;
    twoFactorState.tmpId = "";
    if (passwordLabel) {
      passwordLabel.innerHTML = 'Passwort <span class="text-rose-400">*</span>';
    }
    if (passwordHint) {
      passwordHint.textContent = "Tipp: Nutze ein starkes, einzigartiges Passwort – am besten mit einem Passwort-Manager.";
    }
    if (twoFactorPrompt) {
      twoFactorPrompt.textContent = "";
      twoFactorPrompt.classList.add("hidden");
    }
    if (forgotLink) {
      forgotLink.classList.remove("hidden");
    }
    if (toggleBtn) {
      toggleBtn.classList.remove("hidden");
      toggleBtn.setAttribute("aria-pressed", "false");
      toggleBtn.setAttribute("aria-label", "Passwort anzeigen oder verbergen");
    }
    if (toggleText) {
      toggleText.textContent = "Anzeigen";
    }
    toggleIconPath?.setAttribute("d", "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z");
    toggleIconCircle?.setAttribute("r", "3");
    if (secretInput) {
      secretInput.value = "";
      secretInput.type = "password";
      secretInput.placeholder = "Dein Passwort";
      secretInput.setAttribute("autocomplete", "current-password");
      secretInput.removeAttribute("inputmode");
    }
    setSubmitLabel(false);
  };

  const activateTwoFactorMode = (normalized) => {
    const tmpId = typeof normalized?.data?.tmp_id === "string"
      ? normalized.data.tmp_id.trim()
      : "";
    if (!tmpId) {
      return false;
    }

    const verification = normalized?.data?.verification;
    const prompt = typeof verification?.prompt === "string"
      ? verification.prompt.trim()
      : "";

    twoFactorState.active = true;
    twoFactorState.tmpId = tmpId;

    if (passwordLabel) {
      passwordLabel.innerHTML = 'Verifizierungscode <span class="text-rose-400">*</span>';
    }
    if (passwordHint) {
      passwordHint.textContent = "Gib jetzt den Einmalcode aus deiner 2FA-Methode ein.";
    }
    if (twoFactorPrompt) {
      twoFactorPrompt.textContent = prompt || "Zwei-Faktor-Verifizierung erforderlich.";
      twoFactorPrompt.classList.remove("hidden");
    }
    if (forgotLink) {
      forgotLink.classList.add("hidden");
    }
    if (toggleBtn) {
      toggleBtn.classList.add("hidden");
    }
    if (secretInput) {
      secretInput.value = "";
      secretInput.type = "text";
      secretInput.placeholder = "Verifizierungscode";
      secretInput.setAttribute("autocomplete", "one-time-code");
      secretInput.setAttribute("inputmode", "numeric");
      secretInput.focus();
    }
    setSubmitLabel(true);
    return true;
  };

  resetTwoFactorMode();

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (isSubmitting) {
      if (window.console?.warn) {
        console.warn("Login already in progress, ignoring duplicate submit");
      }
      return;
    }

    isSubmitting = true;

    const usr = emailInput?.value?.trim() || "";
    const secret = secretInput?.value || "";

    if (!secret) {
      const msg = twoFactorState.active
        ? "Bitte gib deinen Verifizierungscode ein."
        : "Bitte gib E-Mail und Passwort ein.";
      showToast?.(msg, "error", 6000);
      isSubmitting = false;
      return;
    }

    if (!twoFactorState.active && !usr) {
      showToast?.("Bitte gib E-Mail und Passwort ein.", "error", 6000);
      isSubmitting = false;
      return;
    }

    if (btn) {
      btn.disabled = true;
    }
    spinner?.classList.remove("hidden");
    if (btnText) {
      btnText.textContent = twoFactorState.active ? "Code wird geprüft ..." : "Einloggen ...";
    }
    showToast?.(
      twoFactorState.active ? "Verifizierung läuft ..." : "Login läuft ...",
      "loading",
      7000,
    );

    try {
      const body = twoFactorState.active
        ? { otp: secret, tmp_id: twoFactorState.tmpId }
        : { usr, pwd: secret };

      const { normalized } = await fetchJson(LOGIN_ENDPOINT, {
        method: "POST",
        body,
        headers: { "X-Idempotency-Key": createIdempotencyKey() },
      });

      if (normalized?.success) {
        resetTwoFactorMode();
        removeToast?.();
        showToast?.("Login erfolgreich! Weiterleitung ...", "success", 1500);
        setTimeout(() => {
          const redirectTo = typeof normalized?.data?.redirect_to === "string"
            ? normalized.data.redirect_to.trim()
            : "";
          const homePage = typeof normalized?.data?.home_page === "string"
            ? normalized.data.home_page.trim()
            : "";
          const target = redirectTo || redirectHint || homePage || "/start";
          window.location.replace(safeRedirectUrl(target, "/start"));
        }, 600);
      } else {
        removeToast?.();
        const normalizedToast = typeof normalized?.toast === "string" ? normalized.toast.trim() : "";
        const errorMsg = normalizedToast || "Login fehlgeschlagen.";
        const errorCode = typeof normalized?.code === "string" ? normalized.code.toLowerCase() : "";
        const isRateLimited = errorCode === "rate_limited" || errorCode === "http_429" || errorCode === "too_many_requests";
        const errorMsgLower = String(errorMsg).toLowerCase();

        if (errorCode === "two_factor_required") {
          const activated = activateTwoFactorMode(normalized);
          if (!activated) {
            resetTwoFactorMode();
            showToast?.("Zwei-Faktor-Verifizierung konnte nicht gestartet werden.", "error", 7000);
          } else {
            showToast?.(errorMsg || "Zwei-Faktor-Verifizierung erforderlich.", "warning", 7000);
          }
        } else if (errorCode === "password_reset_required") {
          const redirectTo = typeof normalized?.data?.redirect_to === "string"
            ? normalized.data.redirect_to.trim()
            : "/update-password";
          safeRedirect(redirectTo, "/update-password");
        } else if (twoFactorState.active && (errorCode === "login_failed" || errorCode === "unauthorized")) {
          showToast?.("Verifizierungscode ungültig oder abgelaufen.", "error", 7000);
          if (secretInput) {
            secretInput.value = "";
            secretInput.focus();
          }
        } else if (isRateLimited || errorMsgLower.includes("zu viele")) {
          showToast?.("Zu viele Login-Versuche. Bitte warte einige Minuten.", "error", 10000);
        } else {
          showToast?.(errorMsg, "error", 6000);
          if (secretInput) {
            secretInput.value = "";
            secretInput.focus();
          }
        }
      }
    } catch (error) {
      removeToast?.();
      const errorMessage = typeof error?.message === "string"
        ? error.message
        : (typeof error === "string" ? error : "");
      showToast?.(errorMessage || "Unerwarteter Fehler.", "error", 6000);
    } finally {
      isSubmitting = false;
      if (btn) {
        btn.disabled = false;
      }
      spinner?.classList.add("hidden");
      setSubmitLabel(twoFactorState.active);
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initLoginPage, { once: true });
} else {
  initLoginPage();
}
